import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import Tesseract from 'tesseract.js';
import { api } from '../api';
import { defaultSheet, SCOPE_LABEL } from '../datasheet';
import EditorView from './EditorView';
import Modal from './Modal';
import { FileUpload } from './ImageUpload';

// Worker matched to the installed lib version (avoids version-mismatch errors).
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const IMAGE_TARGETS = [
  { v: 'productImage', label: 'Product Image' },
  { v: 'dimensionsImage', label: 'Dimensions Drawing' },
  { v: 'extraImage', label: 'Extra Image' },
  { v: 'photometric', label: 'Photometric chart (add)' },
  { v: 'bezel', label: 'Bezel swatch (add)' },
  { v: 'accessory', label: 'Accessory image (add)' },
];
const TEXT_TARGETS = [
  { v: 'name', label: 'Product Name' }, { v: 'code', label: 'Model Code' }, { v: 'variant', label: 'Variant Tag' },
  { v: 'pd.lightOutput', label: 'Detail: Light Output' }, { v: 'pd.weight', label: 'Detail: Weight' },
  { v: 'pd.efficacy', label: 'Detail: Efficacy' }, { v: 'pd.ugr', label: 'Detail: UGR' },
  { v: 'cs.cri', label: 'Spec: CRI' }, { v: 'cs.cct', label: 'Spec: CCT' },
  { v: 'cs.voltage', label: 'Spec: Voltage' }, { v: 'cs.dimming', label: 'Spec: Dimming' },
];

export default function MigrationWorkbench({ sheet, setSheet, currentId, setCurrentId, toast }) {
  const [legacy, setLegacy] = useState([]);
  const [legacyQ, setLegacyQ] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [scale, setScale] = useState(1.3);
  const [sel, setSel] = useState(null);
  const [crop, setCrop] = useState('');
  const [ocr, setOcr] = useState('');
  const [ocrBusy, setOcrBusy] = useState(false);
  const [imgTarget, setImgTarget] = useState('dimensionsImage');
  const [textTarget, setTextTarget] = useState('name');
  const [families, setFamilies] = useState([]);
  const [scopeModal, setScopeModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  const refresh = useCallback(async () => {
    try { const { items } = await api.listLegacy(legacyQ); setLegacy(items); }
    catch (e) { console.error('Failed to load legacy datasheets', e); }
  }, [legacyQ]);
  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    (async () => { try { const f = await api.listFamilies(); setFamilies(f.items || []); } catch (e) { console.warn('Families load failed', e); } })();
  }, []);

  // ---- save the built datasheet into the library ----
  async function onSaveClick() {
    if (currentId) {
      setSaving(true);
      try {
        await api.updateProduct(currentId, { name: sheet.name || '(Untitled)', code: sheet.code || '', data: sheet, family: sheet.family || null });
        toast('Saved');
      } catch (e) { toast('Save failed: ' + (e.message || e), true); }
      setSaving(false);
    } else {
      setScopeModal(true);
    }
  }
  async function saveToScope(sc) {
    setScopeModal(false); setSaving(true);
    try {
      const row = await api.createProduct({ name: sheet.name || '(Untitled)', code: sheet.code || '', data: sheet, scope: sc, family: sheet.family || null });
      setCurrentId(row.id);
      toast('Saved to ' + SCOPE_LABEL[sc]);
    } catch (e) { toast('Save failed: ' + (e.message || e), true); }
    setSaving(false);
  }
  function newSheet() { setSheet(defaultSheet()); setCurrentId(null); toast('Started a new datasheet'); }

  // (Re)render whenever the source/page/scale changes.
  useEffect(() => {
    let cancelled = false;
    async function render() {
      const canvas = canvasRef.current; if (!canvas) return;
      if (pdfDoc) {
        const pg = await pdfDoc.getPage(page);
        const viewport = pg.getViewport({ scale });
        if (cancelled) return;
        canvas.width = viewport.width; canvas.height = viewport.height;
        await pg.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      } else if (imgRef.current) {
        const img = imgRef.current;
        canvas.width = img.naturalWidth * scale; canvas.height = img.naturalHeight * scale;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      }
    }
    render();
    return () => { cancelled = true; };
  }, [pdfDoc, page, scale, activeId]);

  async function onUpload(file) {
    if (!file) return;
    const name = window.prompt('Name this old datasheet:', (file.name || '').replace(/\.[^.]+$/, ''));
    if (name === null) return;
    try {
      const meta = await api.uploadLegacy(file, name || file.name, 'upload');
      await refresh();
      await openLegacy(meta.id, meta);
      toast('Uploaded "' + (name || file.name) + '"');
    } catch (e) { toast('Upload failed: ' + (e.message || e), true); }
  }

  async function openLegacy(id, metaMaybe) {
    setActiveId(id); setSel(null); setCrop(''); setOcr('');
    const meta = metaMaybe || legacy.find(l => l.id === id) || {};
    let url;
    try { url = await api.getLegacyObjectUrl(id); } catch (e) { toast('Could not load file', true); return; }
    const isPdf = (meta.content_type || '').includes('pdf') || /\.pdf$/i.test(meta.name || '');
    if (isPdf) {
      try {
        const buf = await (await fetch(url)).arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data: buf }).promise;
        imgRef.current = null; setPdfDoc(doc); setNumPages(doc.numPages); setPage(1);
      } catch (e) { toast('PDF render failed: ' + (e.message || e), true); }
    } else {
      const img = new Image();
      img.onload = () => { imgRef.current = img; setPdfDoc(null); setNumPages(1); setPage(1); };
      img.onerror = () => toast('Image load failed', true);
      img.src = url;
    }
  }

  async function removeLegacy(id, e) {
    e.stopPropagation();
    if (!window.confirm('Remove this old datasheet?')) return;
    await api.deleteLegacy(id);
    if (activeId === id) { setActiveId(null); setPdfDoc(null); imgRef.current = null; }
    refresh();
  }

  // ---- selection / cropping ----
  function stageXY(e) {
    const r = stageRef.current.getBoundingClientRect();
    const c = canvasRef.current;
    const x = Math.max(0, Math.min(c.width, e.clientX - r.left));
    const y = Math.max(0, Math.min(c.height, e.clientY - r.top));
    return { x, y };
  }
  function onDown(e) { if (!canvasRef.current) return; const p = stageXY(e); dragRef.current = p; setSel({ x: p.x, y: p.y, w: 0, h: 0 }); setCrop(''); setOcr(''); }
  function onMove(e) {
    if (!dragRef.current) return;
    const p = stageXY(e); const s = dragRef.current;
    setSel({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
  }
  function onUp() {
    if (!dragRef.current) return;
    dragRef.current = null;
    setSel(cur => { if (cur && cur.w > 5 && cur.h > 5) makeCrop(cur); return cur; });
  }
  function makeCrop(r) {
    const c = canvasRef.current;
    const off = document.createElement('canvas');
    off.width = Math.round(r.w); off.height = Math.round(r.h);
    off.getContext('2d').drawImage(c, r.x, r.y, r.w, r.h, 0, 0, r.w, r.h);
    setCrop(off.toDataURL('image/png'));
  }

  // ---- send actions ----
  function sendImage() {
    if (!crop) return;
    if (imgTarget === 'productImage' || imgTarget === 'dimensionsImage' || imgTarget === 'extraImage') {
      setSheet(s => ({ ...s, [imgTarget]: crop }));
    } else if (imgTarget === 'photometric') {
      setSheet(s => ({ ...s, photometrics: [...(s.photometrics || []), { label: 'Imported', image: crop }] }));
    } else if (imgTarget === 'bezel') {
      setSheet(s => ({ ...s, bezelOptions: [...(s.bezelOptions || []), { name: 'Imported', image: crop }] }));
    } else if (imgTarget === 'accessory') {
      setSheet(s => ({ ...s, accessories: [...(s.accessories || []), { name: 'Imported', code: '', image: crop }] }));
    }
    toast('Sent crop → ' + (IMAGE_TARGETS.find(t => t.v === imgTarget) || {}).label);
  }
  async function runOcr() {
    if (!crop) return;
    setOcrBusy(true); setOcr('');
    try { const { data: { text } } = await Tesseract.recognize(crop, 'eng'); setOcr((text || '').trim()); }
    catch (e) { toast('OCR failed: ' + (e.message || e), true); }
    setOcrBusy(false);
  }
  function sendText() {
    const val = (ocr || '').trim(); if (!val) return;
    setSheet(s => {
      const n = { ...s };
      if (textTarget.startsWith('pd.')) n.productDetails = { ...n.productDetails, [textTarget.slice(3)]: val };
      else if (textTarget.startsWith('cs.')) n.commonSpecs = { ...n.commonSpecs, [textTarget.slice(3)]: val };
      else n[textTarget] = val;
      return n;
    });
    toast('Sent text → ' + (TEXT_TARGETS.find(t => t.v === textTarget) || {}).label);
  }
  function copyText() { if (ocr && navigator.clipboard) navigator.clipboard.writeText(ocr).then(() => toast('Copied'), () => {}); }

  return (
    <div className="mw-layout">
      <div className="mw-toolbar">
        <FileUpload className="btn btn-primary" testid="mw-upload" label="Upload old datasheet" accept="image/*,application/pdf" onFiles={files => onUpload(files[0])} />
        <span className="field-hint" style={{ margin: 0 }}>PDF or image. Drag a box on the left to crop a region, then send it to the new datasheet on the right.</span>
        <input className="input" data-testid="mw-search" style={{ maxWidth: 220, marginLeft: 'auto' }} placeholder="Search old datasheets…" value={legacyQ} onChange={e => setLegacyQ(e.target.value)} />
        <div className="mw-legacy">
          {legacy.length === 0 ? <span className="field-hint" style={{ margin: 0 }}>{legacyQ ? 'No matches.' : 'No old datasheets yet.'}</span> : legacy.map(l => (
            <div key={l.id} className={'mw-legacy-item' + (l.id === activeId ? ' active' : '')} onClick={() => openLegacy(l.id)}>
              {l.name}<span style={{ color: 'var(--text-muted)', marginLeft: 6 }} onClick={e => removeLegacy(l.id, e)}>✕</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mw-split">
        {/* LEFT — old datasheet viewer + cropper */}
        <div className="mw-old">
          <div className="mw-old-head">
            {pdfDoc && numPages > 1 && (
              <>
                <button className="btn btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>‹</button>
                <span style={{ fontSize: 12 }}>Page {page}/{numPages}</span>
                <button className="btn btn-sm" onClick={() => setPage(p => Math.min(numPages, p + 1))} disabled={page >= numPages}>›</button>
              </>
            )}
            <button className="btn btn-sm" onClick={() => setScale(s => Math.max(0.4, s - 0.2))}>−</button>
            <span style={{ fontSize: 12 }}>{Math.round(scale * 100)}%</span>
            <button className="btn btn-sm" onClick={() => setScale(s => Math.min(4, s + 0.2))}>+</button>
            {sel && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>selection {Math.round(sel.w)}×{Math.round(sel.h)}</span>}
          </div>
          <div className="mw-canvas-wrap">
            {!activeId ? (
              <div className="empty-note" style={{ color: '#cfd4da', borderColor: '#4b5563', margin: 'auto' }}>Upload or pick an old datasheet to begin.</div>
            ) : (
              <div className="mw-canvas-stage" ref={stageRef} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp} style={{ cursor: 'crosshair' }}>
                <canvas ref={canvasRef} />
                {sel && <div className="mw-sel" style={{ left: sel.x, top: sel.y, width: sel.w, height: sel.h }} />}
              </div>
            )}
          </div>
          {crop && (
            <div className="mw-crop-panel">
              <img src={crop} alt="crop" className="mw-crop-preview" />
              <div className="mw-actions">
                <select className="select" style={{ width: 'auto' }} value={imgTarget} onChange={e => setImgTarget(e.target.value)}>
                  {IMAGE_TARGETS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
                <button className="btn btn-primary btn-sm" onClick={sendImage}>→ Send image</button>
                <button className="btn btn-sm" onClick={runOcr} disabled={ocrBusy}>{ocrBusy ? 'Reading…' : 'Extract text (OCR)'}</button>
              </div>
              {ocr !== '' && (
                <div style={{ marginTop: 8 }}>
                  <textarea className="textarea" value={ocr} onChange={e => setOcr(e.target.value)} style={{ minHeight: 48 }} />
                  <div className="mw-actions" style={{ marginTop: 6 }}>
                    <select className="select" style={{ width: 'auto' }} value={textTarget} onChange={e => setTextTarget(e.target.value)}>
                      {TEXT_TARGETS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                    </select>
                    <button className="btn btn-primary btn-sm" onClick={sendText}>→ Send text</button>
                    <button className="btn btn-sm" onClick={copyText}>Copy</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — the new datasheet generator, live */}
        <div className="mw-new">
          <div className="mw-new-head" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ marginRight: 'auto' }}>New datasheet (build it from the old one →)</span>
            <label style={{ fontSize: 11, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Family</label>
            <select className="select" data-testid="mw-family-select" style={{ width: 'auto', maxWidth: 180 }} value={sheet.family || ''} onChange={e => setSheet(s => ({ ...s, family: e.target.value }))} title="Assign to a Core Range family">
              <option value="">— no family</option>
              {families.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
              {sheet.family && !families.find(f => f.name === sheet.family) && <option value={sheet.family}>{sheet.family}</option>}
            </select>
            <button className="btn btn-sm" data-testid="mw-new-btn" onClick={newSheet}>＋ New</button>
            <button className="btn btn-primary btn-sm" data-testid="mw-save-btn" onClick={onSaveClick} disabled={saving}>{saving ? 'Saving…' : (currentId ? 'Save changes' : 'Save to library')}</button>
          </div>
          <div className="mw-new-body">
            <EditorView sheet={sheet} setSheet={setSheet} />
          </div>
        </div>
      </div>

      <Modal open={scopeModal} title="Save datasheet to…" onClose={() => setScopeModal(false)} testid="mw-scope-modal"
        actions={<button className="btn" onClick={() => setScopeModal(false)}>Cancel</button>}>
        <p className="modal-hint">Choose which library this converted datasheet belongs to{sheet.family ? <> · family <strong>{sheet.family}</strong></> : null}.</p>
        <div className="scope-choices">
          <button className="scope-choice" data-testid="mw-scope-core" onClick={() => saveToScope('core')}><strong>Core Range</strong><span>Shared standard products</span></button>
          <button className="scope-choice" data-testid="mw-scope-customer" onClick={() => saveToScope('customer')}><strong>Customer Specific</strong><span>Bespoke / customer items</span></button>
          <button className="scope-choice" data-testid="mw-scope-personal" onClick={() => saveToScope('personal')}><strong>Personal / Project</strong><span>Your working drafts</span></button>
        </div>
      </Modal>
    </div>
  );
}
